import { FC, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useDebounce } from "usehooks-ts";

import { Badge } from "../../../../components/badge/Badge";
import { SettingsInput } from "../../../../components/inputs/SettingsInput";
import { FieldLabel } from "../../../../components/newUI/text";
import { RoleMembersPreview } from "../../../../components/select/previews/RoleMembersPreview";
import CustomSelect from "../../../../components/select/Select";
import { getInitialsFromFullName } from "../../../../constants/constants";
import { RoleModel } from "../../../../factories/role/role.model";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { useGetRolesQuery } from "../../../../slices/RoleSlice";
import { useLazySearchTemplateQuery } from "../../../../slices/TemplatesSlice";
import { useGetUsersQuery } from "../../../../slices/UserSlice";
import { SelectOptionType } from "../../../../types/types";
import {
  DUE_DAYS_MAX,
  DUE_DAYS_MIN,
  parseDueDays,
} from "../../../../utils/dueDays";
import { compareSelectOptionsAlphabeticallyWithNumbersFirst } from "../../../../utils/sort";
import { WorkTypeDefaultValuesSectionProps } from "../model/workTypeEditor.types";

const BILLABLE_OPTIONS: SelectOptionType[] = [
  { value: "billable", label: "Billable" },
  { value: "nonBillable", label: "Non-billable" },
  { value: "none", label: "No default (person decides)" },
];

const NO_ROLE_OPTION: SelectOptionType = {
  value: "",
  label: "No role",
};

const findOption = (
  options: SelectOptionType[],
  value?: string | null,
  fallback?: SelectOptionType | null,
) => {
  if (!value) {
    return null;
  }

  return options.find((option) => option.value === value) ?? fallback ?? null;
};

const getRole = (roles: RoleModel[] | undefined, roleId: string | null) => {
  return roles?.find((role) => role.id === roleId);
};

export const WorkTypeDefaultValuesSection: FC<
  WorkTypeDefaultValuesSectionProps
> = ({ isVisible, control, workType }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const [searchTemplate, setSearchTemplate] = useState("");
  const searchTemplateDebounce = useDebounce(searchTemplate, 200);
  const defaultData = workType?.defaultWorkTypeData;

  const [getTemplates, { data: templates, isFetching: isLoadingTemplates }] =
    useLazySearchTemplateQuery();
  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery(orgId, {
    skip: !orgId || !isVisible,
  });
  const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery(
    { orgId },
    { skip: !orgId || !isVisible },
  );

  useEffect(() => {
    if (!orgId || !isVisible) {
      return;
    }

    getTemplates({
      orgId,
      query: searchTemplateDebounce || null,
    });
  }, [getTemplates, isVisible, orgId, searchTemplateDebounce]);

  const templateOptions = useMemo<SelectOptionType[]>(() => {
    const options =
      templates
        ?.map((template) => ({
          value: template.id,
          label: template.templateName ?? "",
        }))
        .sort(compareSelectOptionsAlphabeticallyWithNumbersFirst) || [];
    const defaultTemplate = defaultData?.defaultTemplate;

    if (
      defaultTemplate &&
      !options.some((option) => option.value === defaultTemplate.id)
    ) {
      return [
        {
          value: defaultTemplate.id,
          label: defaultTemplate.templateName,
        },
        ...options,
      ];
    }

    return options;
  }, [defaultData?.defaultTemplate, templates]);

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    const options: SelectOptionType[] = (users || []).map((user) => {
      const firstName = user?.userProfile?.firstName || "";
      const lastName = user?.userProfile?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();

      return {
        avatar: getInitialsFromFullName(fullName),
        label: fullName,
        value: `${user?.id}`,
        type: "custom",
        avatarCustomColor: user.userProfile.color,
      };
    });

    const defaultAssignee = defaultData?.defaultAssignee;

    if (
      defaultAssignee &&
      !options.some((option) => option.value === defaultAssignee.id)
    ) {
      const firstName = defaultAssignee.userProfile?.firstName || "";
      const lastName = defaultAssignee.userProfile?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const label = fullName || defaultAssignee.userProfile?.email || "Unknown";

      return [
        {
          avatar: getInitialsFromFullName(label),
          label,
          value: defaultAssignee.id,
          type: "custom",
          avatarCustomColor: defaultAssignee.userProfile?.color || undefined,
        },
        ...options,
      ];
    }

    return options;
  }, [defaultData?.defaultAssignee, users]);

  const roleOptions = useMemo<SelectOptionType[]>(() => {
    const options = (roles || []).map((role) => ({
      value: role.id,
      label: role.roleName,
    }));
    const defaultRole = defaultData?.defaultRole;

    if (
      defaultRole &&
      !options.some((option) => option.value === defaultRole.id)
    ) {
      options.unshift({
        value: defaultRole.id,
        label: defaultRole.roleName,
      });
    }

    return [NO_ROLE_OPTION, ...options];
  }, [defaultData?.defaultRole, roles]);

  const renderRoleMembersPreview = (option: SelectOptionType) => {
    if (!option.value) {
      return null;
    }

    return <RoleMembersPreview role={getRole(roles, option.value)} />;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={"mt-5"}>
      <div
        className={
          "grid grid-cols-1 gap-4 lg:grid-cols-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_140px]"
        }>
        <div>
          <FieldLabel label={"Default Subtask Template"} />
          <Controller
            control={control}
            name={"defaultTemplateId"}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                options={templateOptions}
                value={findOption(templateOptions, value)}
                inputValue={searchTemplate}
                onChangeInput={setSearchTemplate}
                onChange={(newValue) => {
                  const option = newValue as SelectOptionType | null;

                  onChange(option?.value || null);
                }}
                onClear={() => onChange(null)}
                withMenu
                isClearable
                isLoading={isLoadingTemplates}
                selectStyle={{ fullWidth: true, size: "md" }}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel label={"Default Assignee"} />
          <Controller
            control={control}
            name={"defaultAssigneeId"}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                options={usersOptions}
                value={findOption(usersOptions, value)}
                onChange={(newValue) => {
                  const option = newValue as SelectOptionType | null;

                  onChange(option?.value || null);
                }}
                onClear={() => onChange(null)}
                withMenu
                withAvatar
                isClearable
                isLoading={isLoadingUsers}
                selectStyle={{ fullWidth: true, size: "md" }}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel label={"Default Role"} />
          <Controller
            control={control}
            name={"defaultRoleId"}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                options={roleOptions}
                value={value === null ? null : findOption(roleOptions, value)}
                onChange={(newValue) => {
                  const option = newValue as SelectOptionType | null;

                  onChange(option?.value || null);
                }}
                onClear={() => onChange(null)}
                renderOptionPreview={renderRoleMembersPreview}
                withMenu
                isClearable
                isLoading={isLoadingRoles}
                selectStyle={{ fullWidth: true, size: "md" }}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel label={"Default Budget"} />
          <Controller
            control={control}
            name={"defaultBudget"}
            render={({ field: { value, onChange } }) => (
              <div className={"flex items-center gap-2"}>
                <SettingsInput
                  value={value}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder={"0"}
                  inputClasses={"h-[36px] w-[84px]"}
                  containerClasses={"shrink-0"}
                  type={"number"}
                  min={0}
                  step={1}
                  size={"md"}
                />
                <Badge
                  label={"hrs"}
                  variant={"outline"}
                  size={"sm"}
                  className={"h-[36px] border-[#C8D2DE] px-3"}
                  labelClassName={"text-[#5F758F]"}
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className={"mt-4 flex flex-wrap items-start gap-4"}>
        <div className={"w-full max-w-[420px]"}>
          <FieldLabel label={"Time Billing"} />
          <Controller
            control={control}
            name={"billableDefault"}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                options={BILLABLE_OPTIONS}
                value={findOption(BILLABLE_OPTIONS, value)}
                onChange={(newValue) => {
                  const option = newValue as SelectOptionType | null;

                  onChange(option?.value || "none");
                }}
                withMenu
                selectStyle={{ fullWidth: true, size: "md" }}
              />
            )}
          />
          <div className={"mt-1.5 text-xs text-[#5F758F]"}>
            Whether new time tracked on this work type starts as billable. It
            can still be switched on each time entry.
          </div>
        </div>

        <div className={"w-full max-w-[320px]"}>
          <FieldLabel label={"Default Due Date"} />
          <Controller
            control={control}
            name={"defaultDueDays"}
            render={({ field: { value, onChange } }) => {
              const check = parseDueDays(value);

              return (
                <>
                  <div className={"flex items-center gap-2"}>
                    <SettingsInput
                      value={value}
                      onChange={(event) => onChange(event.target.value)}
                      placeholder={"–"}
                      inputClasses={"h-[36px] w-[84px]"}
                      containerClasses={"shrink-0"}
                      type={"number"}
                      min={DUE_DAYS_MIN}
                      max={DUE_DAYS_MAX}
                      step={1}
                      size={"md"}
                      isError={!check.ok}
                    />
                    <span className={"text-sm font-medium text-[#5F758F]"}>
                      days after start
                    </span>
                  </div>
                  <div
                    className={`mt-1.5 text-xs ${
                      check.ok ? "text-[#5F758F]" : "text-[#DC2626]"
                    }`}>
                    {check.ok
                      ? "New work gets this due date automatically (start date + days). Leave empty for no default."
                      : check.error}
                  </div>
                </>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
